import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Menu, Moon, Sun, X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import logo from '../images/logo.png';
import './Navbar.css';

// Contact is the call to action, so it is styled as a button instead of a plain link.
const links = [
  { id: 'about', label: 'About' },
  { id: 'experience', label: 'Experience' },
  { id: 'skills', label: 'Skills' },
  { id: 'projects', label: 'Projects' },
  { id: 'certificates', label: 'Certifications' }
];
const SECTION_IDS = [...links.map((l) => l.id), 'contact'];

const Navbar = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  const reduceMotion = useReducedMotion();

  const [active, setActive] = useState('about');
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const navRef = useRef(null);
  const menuButtonRef = useRef(null);

  // While a click is scrolling the page to its target, the pill is already there. The spy is paused
  // (locked) so it does not drag the pill through every section on the way, and resumes once the
  // scroll has been quiet for a moment.
  const lockedRef = useRef(false);
  const lockTimer = useRef(null);
  const updateRef = useRef(() => {});

  // Scroll-spy: the active section is the last one whose top has passed ~35% of the viewport.
  useEffect(() => {
    let ticking = false;

    const update = () => {
      ticking = false;
      if (lockedRef.current) return;

      const line = window.innerHeight * 0.35;
      let current = SECTION_IDS[0];
      SECTION_IDS.forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= line) current = id;
      });
      // At the very bottom of the page the last section wins, even if it is short.
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) {
        current = SECTION_IDS[SECTION_IDS.length - 1];
      }
      setActive(current);
      setScrolled(window.scrollY > 24);
    };
    updateRef.current = update;

    const onScroll = () => {
      if (lockedRef.current) {
        // still travelling to the clicked section: resume once the scroll goes quiet
        clearTimeout(lockTimer.current);
        lockTimer.current = setTimeout(() => {
          lockedRef.current = false;
          update();
        }, 140);
        return;
      }
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      clearTimeout(lockTimer.current);
    };
  }, []);

  // Mobile menu: close on Escape, on a tap outside the bar, and when the layout switches to desktop.
  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKey = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    const onPointer = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setMenuOpen(false);
    };

    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [menuOpen]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 901px)');
    const onChange = () => {
      if (mq.matches) setMenuOpen(false);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const go = (id) => (e) => {
    e.preventDefault();
    setMenuOpen(false);

    // Move the pill immediately and lock the spy until the scroll settles.
    setActive(id);
    lockedRef.current = true;
    clearTimeout(lockTimer.current);
    // fallback: if no scroll happens (already there), release shortly after
    lockTimer.current = setTimeout(() => {
      lockedRef.current = false;
      updateRef.current();
    }, 300);

    const behavior = reduceMotion ? 'auto' : 'smooth';
    if (id === 'about') {
      window.scrollTo({ top: 0, behavior });
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior });
  };

  const themeLabel = `Switch to ${isDarkMode ? 'light' : 'dark'} mode`;

  return (
    <nav ref={navRef} className={`site-nav ${scrolled ? 'is-scrolled' : ''}`} aria-label="Primary">
      <div className="site-bar">
        {/* Brand: the PC logo plus the name; a link back to the top */}
        <a href="#about" className="site-brand" onClick={go('about')} aria-label="Phani Charan, back to top">
          <img src={logo} alt="" width="36" height="35" />
          <span className="site-brand-text">Phani Charan</span>
        </a>

        {/* Desktop links, with a pill that slides to the active section */}
        <ul className="site-links">
          {links.map((link) => {
            const isActive = active === link.id;
            return (
              <li key={link.id}>
                <a
                  href={`#${link.id}`}
                  className={`site-link ${isActive ? 'is-active' : ''}`}
                  aria-current={isActive ? 'location' : undefined}
                  onClick={go(link.id)}
                >
                  {isActive && (
                    <motion.span
                      layoutId="site-pill"
                      className="site-pill"
                      transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 420, damping: 38 }}
                    />
                  )}
                  <span className="site-link-label">{link.label}</span>
                </a>
              </li>
            );
          })}
        </ul>

        <div className="site-actions">
          <button type="button" className="site-theme" onClick={toggleTheme} aria-label={themeLabel} title={themeLabel}>
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={isDarkMode ? 'sun' : 'moon'}
                className="site-theme-icon"
                initial={reduceMotion ? false : { rotate: -90, opacity: 0, scale: 0.6 }}
                animate={{ rotate: 0, opacity: 1, scale: 1 }}
                exit={reduceMotion ? { opacity: 0 } : { rotate: 90, opacity: 0, scale: 0.6 }}
                transition={{ duration: reduceMotion ? 0 : 0.18 }}
              >
                {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
              </motion.span>
            </AnimatePresence>
          </button>

          <a
            href="#contact"
            className={`site-cta ${active === 'contact' ? 'is-active' : ''}`}
            aria-current={active === 'contact' ? 'location' : undefined}
            onClick={go('contact')}
          >
            Contact
          </a>

          <button
            type="button"
            ref={menuButtonRef}
            className="site-menu-btn"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-controls="site-menu"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

      </div>

      {/* Mobile menu panel, a separate card below the bar */}
      <div id="site-menu" className={`site-menu ${menuOpen ? 'is-open' : ''}`}>
        {links.map((link) => {
          const isActive = active === link.id;
          return (
            <a
              key={link.id}
              href={`#${link.id}`}
              className={`site-menu-link ${isActive ? 'is-active' : ''}`}
              aria-current={isActive ? 'location' : undefined}
              onClick={go(link.id)}
            >
              {link.label}
            </a>
          );
        })}
        <a href="#contact" className="site-cta site-cta-block" onClick={go('contact')}>
          Contact
        </a>
      </div>
    </nav>
  );
};

export default Navbar;
